<template>
  <div
    v-if="hasCurrentStudy"
    class="role-site-selector"
  >
    <!-- Role Selector -->
    <a-dropdown
      v-if="availableRoles.length > 0"
      trigger="click"
      position="br"
    >
      <div class="selector-trigger role-trigger">
        <icon-user class="selector-icon" />
        <span class="selector-text">{{ currentRoleDisplay || '选择角色' }}</span>
        <a-tag
          v-if="currentRoleLevel"
          :color="currentRoleLevel === 'STUDY' ? 'arcoblue' : 'green'"
          size="small"
        >
          {{ currentRoleLevel === 'STUDY' ? '研究级' : '中心级' }}
        </a-tag>
        <icon-down
          v-if="hasMultipleRoles"
          class="arrow-icon"
        />
      </div>

      <template #content>
        <div class="selector-dropdown">
          <div class="dropdown-header">
            选择角色
          </div>
          <div class="dropdown-list">
            <div
              v-for="role in availableRoles"
              :key="role.memberId"
              class="dropdown-item"
              :class="{ active: role.memberId === currentRole?.memberId }"
              @click="handleSwitchRole(role.memberId)"
            >
              <div class="item-main">
                <span class="item-name">{{ role.roleDisplay }}</span>
                <a-tag
                  :color="role.roleLevel === 'STUDY' ? 'arcoblue' : 'green'"
                  size="small"
                >
                  {{ role.roleLevel === 'STUDY' ? '研究级' : '中心级' }}
                </a-tag>
              </div>
              <icon-check
                v-if="role.memberId === currentRole?.memberId"
                class="check-icon"
              />
            </div>
          </div>
        </div>
      </template>
    </a-dropdown>

    <!-- Divider -->
    <span
      v-if="isSiteLevel && availableSites.length > 0"
      class="selector-divider"
    >/</span>

    <!-- Site Selector (only for SITE level roles) -->
    <a-dropdown
      v-if="isSiteLevel && availableSites.length > 0"
      trigger="click"
      position="br"
    >
      <div class="selector-trigger site-trigger">
        <icon-location class="selector-icon" />
        <span class="selector-text">{{ currentSiteDisplay || '选择中心' }}</span>
        <icon-down
          v-if="hasMultipleSites"
          class="arrow-icon"
        />
      </div>

      <template #content>
        <div class="selector-dropdown">
          <div class="dropdown-header">
            选择研究中心
          </div>
          <div class="dropdown-list">
            <div
              v-for="site in availableSites"
              :key="site.id"
              class="dropdown-item"
              :class="{ active: site.id === currentSite?.id }"
              @click="handleSwitchSite(site.id)"
            >
              <div class="item-main">
                <span class="item-name">{{ site.organizationName || site.siteCode }}</span>
                <span
                  v-if="site.organizationName && site.siteCode"
                  class="item-desc"
                >{{ site.siteCode }}</span>
              </div>
              <icon-check
                v-if="site.id === currentSite?.id"
                class="check-icon"
              />
            </div>
          </div>
        </div>
      </template>
    </a-dropdown>
  </div>
</template>

<script setup lang="ts">
import { computed, watchEffect, onMounted } from 'vue'
import { storeToRefs } from 'pinia'
import { useStudyContextStore } from '@/stores/studyContext'
import IconUser from '@arco-design/web-vue/es/icon/icon-user'
import IconLocation from '@arco-design/web-vue/es/icon/icon-location'
import IconDown from '@arco-design/web-vue/es/icon/icon-down'
import IconCheck from '@arco-design/web-vue/es/icon/icon-check'

console.log('[RoleSiteSelector] Component script executing...')

const studyStore = useStudyContextStore()

// 使用 storeToRefs 确保响应式
const {
  hasCurrentStudy,
  availableRoles,
  currentRole,
  currentRoleDisplay,
  currentRoleLevel,
  hasMultipleRoles,
  isSiteLevel,
  availableSites,
  currentSite,
  hasMultipleSites,
} = storeToRefs(studyStore)

// 组件挂载时日志
onMounted(() => {
  console.log('[RoleSiteSelector] Component MOUNTED!', {
    hasCurrentStudy: hasCurrentStudy.value,
    currentRoleLevel: currentRoleLevel.value,
    isSiteLevel: isSiteLevel.value,
    availableSitesCount: availableSites.value.length,
    availableRolesCount: availableRoles.value.length,
    currentRole: currentRole.value,
    availableSites: availableSites.value,
  })
})

// 调试日志
watchEffect(() => {
  console.log('[RoleSiteSelector] watchEffect triggered:', {
    hasCurrentStudy: hasCurrentStudy.value,
    currentRoleLevel: currentRoleLevel.value,
    isSiteLevel: isSiteLevel.value,
    availableSitesCount: availableSites.value.length,
    availableRolesCount: availableRoles.value.length,
  })
})

const currentSiteDisplay = computed(() => {
  if (!currentSite.value) return null
  // 优先显示组织名称，如果没有则显示中心编号
  return currentSite.value.organizationName || currentSite.value.siteCode
})

// Methods
const handleSwitchRole = async (memberId: string) => {
  await studyStore.switchRole(memberId)
}

const handleSwitchSite = (siteId: string) => {
  studyStore.switchSite(siteId)
}
</script>

<style scoped>
.role-site-selector {
  display: flex;
  align-items: center;
  gap: 8px;
}

.selector-trigger {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.2s ease;
  background: transparent;
}

.selector-trigger:hover {
  background: var(--color-fill-2);
}

.selector-icon {
  font-size: 14px;
  color: var(--color-text-2);
}

.selector-text {
  font-size: 13px;
  color: var(--color-text-1);
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.arrow-icon {
  font-size: 12px;
  color: var(--color-text-3);
}

.selector-divider {
  color: var(--color-text-4);
  font-size: 14px;
}

/* Dropdown styles */
.selector-dropdown {
  min-width: 200px;
  max-width: 300px;
}

.dropdown-header {
  padding: 8px 12px;
  font-size: 12px;
  font-weight: 500;
  color: var(--color-text-3);
  border-bottom: 1px solid var(--color-border-2);
}

.dropdown-list {
  max-height: 300px;
  overflow-y: auto;
}

.dropdown-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  cursor: pointer;
  transition: background 0.2s ease;
}

.dropdown-item:hover {
  background: var(--color-fill-2);
}

.dropdown-item.active {
  background: var(--color-primary-light-1);
}

.item-main {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
}

.item-name {
  font-size: 14px;
  color: var(--color-text-1);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.item-desc {
  font-size: 12px;
  color: var(--color-text-3);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.check-icon {
  font-size: 14px;
  color: rgb(var(--primary-6));
  flex-shrink: 0;
  margin-left: 8px;
}

/* Dark theme */
:global([data-theme="dark"]) {
  .selector-trigger {
    background: var(--color-fill-2);
  }

  .selector-trigger:hover {
    background: var(--color-fill-3);
  }
}

/* Responsive */
@media (max-width: 768px) {
  .role-site-selector {
    display: none;
  }
}
</style>
